// Colorize a segment-icon SVG string for injection into the wheel's
// <foreignObject> icon slots.
//
// Extracted from CreatePieChart (createPie.js) so the parser choke point can be
// unit-tested in isolation (importing createPie pulls in the whole
// SegmentBuilder/Froala chain, which the jest CSS transform cannot handle).
// This module depends only on the DOM globals DOMParser / XMLSerializer.
//
// EXPLORE-02 root-cause guard: DOMParser does NOT throw on empty/malformed
// input — it returns a document whose root is a <parsererror> node
// ("This page contains the following errors: Document is empty"). XMLSerializer
// then faithfully serializes that <parsererror> markup, which callers inject
// via innerHTML, polluting the DOM + accessibility tree with visible error
// nodes. The guards below ensure this function can NEVER emit a parsererror
// node, regardless of which caller passes bad input.
// Bug #969 round 2 (Gordon, verbatim): "Segment icons should NEVER have
// boxes." Round 1 (#4390/#4394) swapped the FaIdBadge/FaIdCard glyphs -- whose
// box is a VISIBLE bordered card frame drawn in the glyph art -- for TbUser.
// But TbUser (like every Tabler icon) bakes an INVISIBLE full-canvas
// bounding-box path into its own markup as a viewBox-padding convention:
// `<path stroke="none" d="M0 0h24v24H0z" fill="none"/>`. That path is dead
// art by design (stroke=none, fill=none) -- until setStrokeColor below walks
// every <path>/<rect> unconditionally and overwrites its stroke, turning the
// invisible frame into a visible colored square outline around the glyph.
// #4394's glyph-swap never touched this because the box isn't in the glyph
// that got swapped OUT, it's baked into the glyph swapped IN. Strip any
// full-canvas background-frame path/rect before colorizing, generically, so
// this class of bug is fixed for every current AND future glyph -- not just
// TbUser -- rather than special-casing one more icon name.
const FRAME_PATH_VARIANTS = (w, h) => [
  `M00h${w}v${h}H0z`,
  `M00h${w}v${h}H0`,
  `M00H${w}V${h}H0Z`,
  `M00H${w}V${h}H0`,
];

const isBackgroundFramePathD = (d, w, h) => {
  if (!d) return false;
  const normalized = d.replace(/[\s,]+/g, "");
  return FRAME_PATH_VARIANTS(w, h).some(
    (variant) => normalized.toLowerCase() === variant.toLowerCase()
  );
};

const isBackgroundFrameRect = (rect, w, h) => {
  const x = parseFloat(rect.getAttribute("x") || "0");
  const y = parseFloat(rect.getAttribute("y") || "0");
  const width = rect.getAttribute("width");
  const height = rect.getAttribute("height");
  return (
    x === 0 &&
    y === 0 &&
    (width === String(w) || width === "100%") &&
    (height === String(h) || height === "100%")
  );
};

const stripBackgroundFramePaths = (svg) => {
  const viewBox = svg.getAttribute("viewBox");
  let w = 24;
  let h = 24;
  if (viewBox) {
    const parts = viewBox.trim().split(/\s+/).map(Number);
    if (parts.length === 4 && parts.every((n) => !Number.isNaN(n))) {
      w = parts[2];
      h = parts[3];
    }
  }
  svg.querySelectorAll("path").forEach((p) => {
    if (isBackgroundFramePathD(p.getAttribute("d"), w, h)) p.remove();
  });
  svg.querySelectorAll("rect").forEach((r) => {
    if (isBackgroundFrameRect(r, w, h)) r.remove();
  });
};

export function changeIconSvgColor(svgString, color) {
  if (typeof svgString !== "string" || !svgString.trim()) {
    return "";
  }
  const parser = new DOMParser();
  const element = parser.parseFromString(svgString, "image/svg+xml");
  if (element.querySelector("parsererror")) {
    return "";
  }
  const iconElement = element.querySelector("svg");
  if (!iconElement) {
    // No <svg> root => nothing meaningful to color/serialize; never inject
    // arbitrary non-icon markup back into a foreignObject.
    return "";
  }

  stripBackgroundFramePaths(iconElement);

  iconElement.setAttribute("width", `100%`);
  iconElement.setAttribute("height", `100%`);
  iconElement.style.display = "flex";
  // react-icons (FontAwesome) render fill-based glyphs using
  // fill="currentColor". Set fill + color on the root so the resolved icon
  // color actually applies; the per-tag stroke pass below still colors line-art
  // icons. (Additive: line-art icons already worked via stroke; this makes
  // fill-based icons honor the color too.)
  // T22.90 (MEASURED on the PR build 2026-10-10, wheel 507722): a line-art
  // (Tabler) icon's root says fill="none" -- it is drawn with strokes only.
  // Overwriting that with the colour filled every closed outline solid, so a
  // "7 in a circle" painted as a plain white disc with the 7 hidden inside, and
  // the wheel no longer matched the outline glyph shown in the picker. Keep
  // fill="none" for stroke-only icons; fill-based icons still get the colour.
  const strokeOnly = (iconElement.getAttribute("fill") || "").trim() === "none";
  if (color) {
    if (!strokeOnly) iconElement.setAttribute("fill", color);
    iconElement.style.color = color;
  }

  const setStrokeColor = (tag, tagColor) => {
    // Guard: never apply an empty/falsy stroke (resolveSegmentIconColor can
    // return "") — that emits an invalid stroke="" and renders the glyph
    // invisible/broken. Skip the stroke pass entirely when color is empty.
    if (!tagColor) return;
    const path = element.querySelectorAll(tag);
    if (path.length) {
      path.forEach((d) => {
        d.setAttribute("stroke", tagColor);
      });
    }
  };

  setStrokeColor("path", color);
  setStrokeColor("polygon", color);
  setStrokeColor("circle", color);
  setStrokeColor("ellipse", color);
  setStrokeColor("line", color);
  setStrokeColor("polyline", color);
  setStrokeColor("rect", color);
  setStrokeColor("text", color);

  const serializer = new XMLSerializer();
  return serializer.serializeToString(element);
}
